import BooleanColumnFilter from '@/components/ColumnFilters/BooleanColumnFilter';
import DateRangeColumnFilter from '@/components/ColumnFilters/DateRangeColumnFilter';
import FilterFlux from '@/components/ColumnFilters/FilterFlux';
import NumberRangeColumnFilter from '@/components/ColumnFilters/NumberRangeColumnFilter';
import SelectColumnFilter from '@/components/ColumnFilters/SelectColumnFilter';
import SusColumnFilter from '@/components/ColumnFilters/SusColumnFilter';
import TextColumnFilter from '@/components/ColumnFilters/TextColumnFilter';
import IngestionsExportButton from '@/components/Ingestions/IngestionsExportButton';
import IngestionStatusBadge from '@/components/Ingestions/IngestionStatusBadge';
import RpuUploadButton from '@/components/Ingestions/RpuUploadButton';
import { useGetIngestionsQuery } from '@/features/api/endpoints/ingestionEndpoints';
import { useGetSusByIdQuery } from '@/features/api/endpoints/suEndpoints';
import PeriodFilter from '@/components/common/PeriodFilter';
import type { ColumnFilterProps, ColumnFilters } from '@/models/flowFilter.type';
import {
  ingestionSortColumns,
  type IngestionRow,
  type IngestionSortColumn,
} from '@/models/ingestion.type';
import useColumnFilters from '@/shared/hooks/useColumnFilters';
import {
  ingestionFilterKinds,
  ingestionRelationColumns,
  INGESTIONS_DIRECTION_PARAM,
  INGESTIONS_PAGE_PARAM,
  INGESTIONS_PAGE_SIZE_PARAM,
  INGESTIONS_SORT_PARAM,
} from '@/shared/utils/ingestion-url';
import { formatNumber } from '@/shared/utils/number-utils';
import { Button, Group, Stack, Text, Title } from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { IconColumns, IconFilterOff } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import {
  DataTable,
  useDataTableColumns,
  type DataTableColumn,
  type DataTableSortStatus,
} from 'mantine-datatable';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

const RECORDS_PER_PAGE_OPTIONS = [25, 50, 100];
const DATE_TIME_SECONDS_FORMAT = 'L LTS';
const EMPTY_VALUE = '—';
const STATUS_OPTIONS = ['SUCCES', 'EN_COURS', 'ERREUR'].map(value => ({ value, label: value }));

const isIngestionSortColumn = (value: unknown): value is IngestionSortColumn =>
  ingestionSortColumns.includes(value as IngestionSortColumn);

// Le volume est une taille de fichier en octets, affichée en kilo-octets (1 Ko = 1 024 octets).
const toKilobytes = (value: number | string): number => Number(value) / 1024;

const formatDateTime = (value: string | null): string =>
  value ? dayjs(value).format(DATE_TIME_SECONDS_FORMAT) : EMPTY_VALUE;

const DEFAULT_SORT: DataTableSortStatus<IngestionRow> = {
  columnAccessor: 'date_heure_debut',
  direction: 'desc',
};
const DEFAULT_RECORDS_PER_PAGE = RECORDS_PER_PAGE_OPTIONS[0];
const RESET_PAGE_PARAMS = [INGESTIONS_PAGE_PARAM];
const DEFAULT_PERIOD_MONTHS = 24;
// Clé localStorage de l'ordre et de la visibilité des colonnes.
const INGESTIONS_COLUMNS_KEY = 'ingestions';
const URL_DATE_FORMAT = 'YYYY-MM-DD';

/** Par défaut, seules les ingestions démarrées dans les 24 derniers mois sont affichées. */
const buildDefaultFilters = (): ColumnFilters => ({
  date_heure_debut: {
    kind: 'date-range',
    from: dayjs().subtract(DEFAULT_PERIOD_MONTHS, 'month').format(URL_DATE_FORMAT),
    to: dayjs().format(URL_DATE_FORMAT),
  },
});

const parsePositiveInteger = (raw: string | null): number | undefined => {
  const value = Number(raw);
  return raw != null && Number.isInteger(value) && value > 0 ? value : undefined;
};

const IngestionsPage = () => {
  const { t } = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tri, pagination et filtres sont dans l'URL : une vue peut être liée depuis une autre page.
  const page = parsePositiveInteger(searchParams.get(INGESTIONS_PAGE_PARAM)) ?? 1;
  const requestedSize = parsePositiveInteger(searchParams.get(INGESTIONS_PAGE_SIZE_PARAM));
  const recordsPerPage =
    requestedSize != null && RECORDS_PER_PAGE_OPTIONS.includes(requestedSize)
      ? requestedSize
      : DEFAULT_RECORDS_PER_PAGE;
  const requestedSort = searchParams.get(INGESTIONS_SORT_PARAM);
  const sortStatus: DataTableSortStatus<IngestionRow> =
    isIngestionSortColumn(requestedSort) && requestedSort !== 'sus'
      ? {
          columnAccessor: requestedSort,
          direction: searchParams.get(INGESTIONS_DIRECTION_PARAM) === 'desc' ? 'desc' : 'asc',
        }
      : DEFAULT_SORT;

  // Une valeur par défaut est retirée de l'URL pour la garder courte.
  const updateParams = useCallback(
    (update: (params: URLSearchParams) => void) =>
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          update(next);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const setPage = (value: number) =>
    updateParams(params => {
      if (value > 1) params.set(INGESTIONS_PAGE_PARAM, String(value));
      else params.delete(INGESTIONS_PAGE_PARAM);
    });

  const setSortStatus = (status: DataTableSortStatus<IngestionRow>) =>
    updateParams(params => {
      params.delete(INGESTIONS_PAGE_PARAM);
      const isDefault =
        status.columnAccessor === DEFAULT_SORT.columnAccessor &&
        status.direction === DEFAULT_SORT.direction;
      if (isDefault) {
        params.delete(INGESTIONS_SORT_PARAM);
        params.delete(INGESTIONS_DIRECTION_PARAM);
      } else {
        params.set(INGESTIONS_SORT_PARAM, String(status.columnAccessor));
        params.set(INGESTIONS_DIRECTION_PARAM, status.direction);
      }
    });

  const setRecordsPerPage = (value: number) =>
    updateParams(params => {
      params.delete(INGESTIONS_PAGE_PARAM);
      if (value === DEFAULT_RECORDS_PER_PAGE) params.delete(INGESTIONS_PAGE_SIZE_PARAM);
      else params.set(INGESTIONS_PAGE_SIZE_PARAM, String(value));
    });

  // Calculée une fois par visite : la période par défaut ne bouge pas pendant la navigation.
  const [defaultFilters] = useState(buildDefaultFilters);
  const {
    filters,
    setFilter: setColumnFilter,
    resetAll: resetAllFilters,
    isFiltering,
    hasFilters,
    flowFilter,
  } = useColumnFilters({
    urlKinds: ingestionFilterKinds,
    relationColumns: ingestionRelationColumns,
    resetParams: RESET_PAGE_PARAMS,
    defaultFilters,
  });

  // Ingestions cochées pour l'export, conservées d'une page à l'autre.
  const [selectedRecords, setSelectedRecords] = useState<IngestionRow[]>([]);

  // La sélection ne survit pas à un changement de filtre : pas de lignes sélectionnées invisibles.
  const setFilter = useCallback(
    (...args: Parameters<typeof setColumnFilter>) => {
      setColumnFilter(...args);
      setSelectedRecords([]);
    },
    [setColumnFilter, setSelectedRecords],
  );
  const resetAll = () => {
    resetAllFilters();
    setSelectedRecords([]);
  };

  // Filtré sur un seul service d'urgence (ex. depuis la page des SU) : son nom passe dans le titre.
  const susFilter = filters.sus;
  const filteredSusId =
    susFilter?.kind === 'select' && susFilter.values.length === 1
      ? Number(susFilter.values[0])
      : NaN;
  // `currentData` (et non `data`) : sans filtre SU, la requête est ignorée et le titre redevient générique.
  const { currentData: filteredSus } = useGetSusByIdQuery(filteredSusId, {
    skip: !Number.isInteger(filteredSusId),
  });
  const pageTitle = filteredSus?.nom
    ? t('admin.ingestions.titleSus', { nom: filteredSus.nom })
    : t('admin.ingestions.title');

  useDocumentTitle(pageTitle);

  const { columnAccessor, direction } = sortStatus;
  const { data, isError, isFetching } = useGetIngestionsQuery({
    page,
    pageSize: recordsPerPage,
    orderBy: isIngestionSortColumn(columnAccessor) ? `${columnAccessor} ${direction}` : undefined,
    filter: flowFilter,
  });

  const columns = useMemo(() => {
    const title = (accessor: IngestionSortColumn): string =>
      t(`admin.ingestions.columns.${accessor}`);

    // Chaque helper renvoie les props `filter` / `filtering` d'une colonne du tableau.
    const filterProps = (
      accessor: IngestionSortColumn,
      renderFilter: (props: ColumnFilterProps) => ReactNode,
    ) => ({
      filter: renderFilter({
        label: title(accessor),
        value: filters[accessor],
        onChange: value => setFilter(accessor, value),
      }),
      filtering: isFiltering(accessor),
    });

    const numberFilter = (accessor: IngestionSortColumn) =>
      filterProps(accessor, props => <NumberRangeColumnFilter {...props} />);
    const dateFilter = (accessor: IngestionSortColumn) =>
      filterProps(accessor, props => <DateRangeColumnFilter {...props} />);
    const textFilter = (accessor: IngestionSortColumn) =>
      filterProps(accessor, props => <TextColumnFilter {...props} />);
    const booleanFilter = (accessor: IngestionSortColumn) =>
      filterProps(accessor, props => <BooleanColumnFilter {...props} />);
    const statusFilter = (accessor: IngestionSortColumn) =>
      filterProps(accessor, props => <SelectColumnFilter {...props} options={STATUS_OPTIONS} />);

    const columnDefinitions = [
      { accessor: 'id', title: title('id'), sortable: true, ...numberFilter('id') },
      {
        accessor: 'date_heure_debut',
        title: title('date_heure_debut'),
        sortable: true,
        textAlign: 'center',
        render: ({ date_heure_debut }) => formatDateTime(date_heure_debut),
      },
      {
        accessor: 'date_heure_fin',
        title: title('date_heure_fin'),
        sortable: true,
        textAlign: 'center',
        ...dateFilter('date_heure_fin'),
        render: ({ date_heure_fin }) => formatDateTime(date_heure_fin),
      },
      {
        accessor: 'fluxname',
        title: title('fluxname'),
        sortable: true,
        ...filterProps('fluxname', props => <FilterFlux {...props} />),
      },
      {
        accessor: 'sus',
        title: title('sus'),
        sortable: false,
        ...filterProps('sus', props => <SusColumnFilter {...props} />),
        render: ({ sus }) =>
          sus == null || sus.length === 0 ? EMPTY_VALUE : sus.map(s => s.nom).join(', '),
      },
      {
        accessor: 'manuel',
        title: title('manuel'),
        sortable: true,
        textAlign: 'center',
        ...booleanFilter('manuel'),
        render: ({ manuel }) =>
          manuel == null
            ? EMPTY_VALUE
            : manuel
              ? t('admin.ingestions.manuel.oui')
              : t('admin.ingestions.manuel.non'),
      },
      {
        accessor: 'emetteur',
        title: title('emetteur'),
        sortable: true,
        textAlign: 'center',
        ...textFilter('emetteur'),
        render: ({ emetteur }) => emetteur ?? EMPTY_VALUE,
      },
      {
        accessor: 'statut',
        title: title('statut'),
        sortable: true,
        textAlign: 'center',
        ...statusFilter('statut'),
        render: ({ statut }) => <IngestionStatusBadge statut={statut} />,
      },
      {
        accessor: 'volume',
        title: title('volume'),
        sortable: true,
        textAlign: 'right',
        ...numberFilter('volume'),
        render: ({ volume }) =>
          volume == null
            ? EMPTY_VALUE
            : t('common.units.kilobyte', { value: formatNumber(toKilobytes(volume)) }),
      },
      {
        accessor: 'nb_element',
        title: title('nb_element'),
        sortable: true,
        textAlign: 'right',
        ...numberFilter('nb_element'),
        render: ({ nb_element }) => formatNumber(nb_element),
      },
      {
        accessor: 'nb_succes',
        title: title('nb_succes'),
        sortable: true,
        textAlign: 'right',
        ...numberFilter('nb_succes'),
        render: ({ nb_succes }) => formatNumber(nb_succes),
      },
      {
        accessor: 'nb_rejet',
        title: title('nb_rejet'),
        sortable: true,
        textAlign: 'right',
        ...numberFilter('nb_rejet'),
        render: ({ nb_rejet }) => formatNumber(nb_rejet),
      },
      {
        accessor: 'nb_erreur',
        title: title('nb_erreur'),
        sortable: true,
        textAlign: 'right',
        ...numberFilter('nb_erreur'),
        render: ({ nb_erreur }) => formatNumber(nb_erreur),
      },
    ] satisfies DataTableColumn<IngestionRow>[];
    // Colonnes déplaçables et masquables, sauf l'id qui reste toujours affiché.
    return columnDefinitions.map(column => ({
      ...column,
      draggable: true,
      toggleable: column.accessor !== 'id',
    }));
  }, [t, filters, setFilter, isFiltering]);

  const { effectiveColumns, resetColumnsOrder, resetColumnsToggle } =
    useDataTableColumns<IngestionRow>({ key: INGESTIONS_COLUMNS_KEY, columns });
  // Colonnes visibles, dans l'ordre affiché : celles de l'onglet « Synthèse » de l'export.
  const exportColumns = effectiveColumns
    .filter(column => !column.hidden)
    .map(column => column.accessor)
    .filter(isIngestionSortColumn);

  const resetColumns = () => {
    resetColumnsOrder();
    resetColumnsToggle();
  };

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Group justify="space-between" align="center" gap="sm">
          <Title order={1}>{pageTitle}</Title>
          <Group gap="sm" wrap="nowrap">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconColumns size={16} stroke={1.5} />}
              onClick={resetColumns}
            >
              {t('admin.ingestions.columns.reset')}
            </Button>
            {hasFilters && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconFilterOff size={16} stroke={1.5} />}
                onClick={resetAll}
              >
                {t('common.filters.resetAll')}
              </Button>
            )}
            {selectedRecords.length > 0 && (
              <IngestionsExportButton
                ingestionIds={selectedRecords.map(({ id }) => id)}
                columns={exportColumns}
              />
            )}
            <RpuUploadButton />
          </Group>
        </Group>

        <PeriodFilter
          value={filters.date_heure_debut}
          onChange={value => setFilter('date_heure_debut', value)}
        />
      </Stack>

      {isError ? (
        <Text c="red" role="alert">
          {t('admin.ingestions.error')}
        </Text>
      ) : (
        <DataTable
          withTableBorder
          withColumnBorders
          minHeight={200}
          fetching={isFetching}
          records={data?.rows}
          idAccessor="id"
          storeColumnsKey={INGESTIONS_COLUMNS_KEY}
          columns={effectiveColumns}
          selectedRecords={selectedRecords}
          onSelectedRecordsChange={setSelectedRecords}
          noRecordsText={t('admin.ingestions.empty')}
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          page={page}
          onPageChange={setPage}
          totalRecords={data?.total}
          recordsPerPage={recordsPerPage}
          recordsPerPageOptions={RECORDS_PER_PAGE_OPTIONS}
          onRecordsPerPageChange={setRecordsPerPage}
          recordsPerPageLabel={t('admin.ingestions.recordsPerPage')}
          paginationWithEdges
          paginationText={({ from, to, totalRecords: total }) =>
            t('admin.ingestions.pagination', { from, to, total })
          }
        />
      )}
    </Stack>
  );
};

export default IngestionsPage;
